import React, { useEffect, useState } from "react";
import { Drawer, Button, Form, Input, Select, message } from "antd";
import { UtilisateurService } from "../Services/UtilisateurService";
import { AdherentsService } from "../Services/AdherentsService";

const { Option } = Select;

const SideBarUser = ({ user, visible, mode, refreshData, closeDrawer }) => {
  const [userRole, setUserRole] = useState([]);
  const [showSocPointFocal, setShowSocPointFocal] = useState(false);
  const [societeOptions, setSocieteOptions] = useState([]);
  const [form] = Form.useForm();
  const gestionUsersService = new UtilisateurService();
  const adherentsService = new AdherentsService();

  useEffect(() => {
    const fetchRole = async () => {
      try {
        const roles = await gestionUsersService.getUserRoles();
        setUserRole(roles);
      } catch (error) {
        console.error("Erreur lors de la récupération des rôles :", error);
      }
    };
    fetchRole();
  }, []);

  useEffect(() => {
    const fetchSocietes = async () => {
      try {
        const societes = await adherentsService.getAllAdherents();
        setSocieteOptions(Array.isArray(societes) ? societes : []);
      } catch (error) {
        console.error("Erreur lors de la récupération des sociétés :", error);
      }
    };

    fetchSocietes();
  }, []);

  useEffect(() => {
    if (user && mode === "edit") {
      form.setFieldsValue({
        ...user,
        login: user.login,
        profil: user.profil || user.role_name,
        socPointFocalCodes: Array.isArray(user.socPointFocals)
          ? user.socPointFocals
            .filter((pointFocal) => pointFocal?.accord)
            .map((pointFocal) => pointFocal?.codadht ?? pointFocal?.codeadht)
            .filter(Boolean)
          : [],
      });

      const currentRoleValue = user?.profil || user?.role_code || user?.role_name;
      setShowSocPointFocal(
        ["role_6", "Point Focal", "Point_Focal"].includes(currentRoleValue)
      );
    } else if (mode === "add") {
      form.setFieldsValue({ pwd_period: "30" });
      setShowSocPointFocal(false);
    } else {
      form.resetFields();
      setShowSocPointFocal(false);
    }
  }, [user, mode, form]);

  const getTitle = () => {
    switch (mode) {
      case "add":
        return "Ajouter un utilisateur";
      case "edit":
        return "Modifier Utilisateur";
      case "changePassword":
        return "Modifier Mot de Passe";
      default:
        return "Gestion des Utilisateurs";
    }
  };

  const onCloseDrawer = () => {
    form.resetFields();
    setShowSocPointFocal(false);
    closeDrawer();
  };

  const onChangeRole = (value) => {
    const selectedRole = userRole.find(
      (role) =>
        role.role_code === value ||
        role.profil === value ||
        role.role_name === value
    );

    const isPointFocalRole =
      value === "role_6" ||
      value === "Point Focal" ||
      value === "Point_Focal" ||
      selectedRole?.role_code === "role_6" ||
      selectedRole?.profil === "role_6" ||
      selectedRole?.role_name === "Point Focal" ||
      selectedRole?.role_name === "Point_Focal";

    setShowSocPointFocal(isPointFocalRole);

    if (!isPointFocalRole) {
      form.setFieldsValue({ socPointFocalCodes: [] });
    }
  };

  const onSubmit = async (values) => {
    const selectedPointFocals = (values.socPointFocalCodes || []).map(String);
    const basePointFocals =
      Array.isArray(user?.socPointFocals) && user.socPointFocals.length > 0
        ? user.socPointFocals
        : societeOptions;

    const payload = {
      ...values,
      login: values.login || user?.login,
      pwd_period: values.pwd_period
        ? parseInt(values.pwd_period, 10)
        : values.pwd_period,
      perim: "IPM",
      ...(showSocPointFocal && Array.isArray(basePointFocals) && basePointFocals.length > 0
        ? {
          socPointFocals: basePointFocals.map((pointFocal) => ({
            login: values.login || user?.login,
            codeadht: pointFocal.codadht ?? pointFocal.codeadht,
            accord: selectedPointFocals.includes(
              String(pointFocal.codadht ?? pointFocal.codeadht)
            ),
          })),
        }
        : {}),
    };

    delete payload.socPointFocalCodes;

    // Correction pour le changement de mot de passe
    if (mode === "changePassword") {
      payload.login = user?.login; // Utilise le login du record sélectionné
    }

    try {
      if (mode === "add") {
        await gestionUsersService.addUser(payload);
        message.success("Utilisateur ajouté avec succès");
      } else if (mode === "edit") {
        await gestionUsersService.updateUser(payload);
        message.success("Utilisateur modifié avec succès");
      } else if (mode === "changePassword") {
        await gestionUsersService.updatePassword(payload);
        message.success("Mot de passe modifié avec succès");
      }
      await refreshData();
      onCloseDrawer();
    } catch (error) {
      console.error("Erreur lors de l'opération:", error);
      message.error("Erreur lors de l'opération");
    }
  };

  const validatePassword = (_, value) => {
    if (!value || form.getFieldValue("new_password") === value) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("Les mots de passe ne correspondent pas!"));
  };

  return (
    <Drawer
      title={getTitle()}
      onClose={onCloseDrawer}
      open={visible}
      width={500}
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        autoComplete="off"
      >
        {mode === "add" && (
          <>
            <Form.Item
              name="login"
              label="Login"
              rules={[{ required: true, message: "Veuillez saisir le login!" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="emailaddress"
              label="Email"
              rules={[
                {
                  required: true,
                  type: "email",
                  message: "Veuillez saisir un email valide!",
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="username"
              label="Prénom & Nom"
              rules={[
                {
                  required: true,
                  message: "Veuillez saisir le prénom et le nom!",
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="profil"
              label="Rôle"
              rules={[
                { required: true, message: "Veuillez sélectionner un rôle!" },
              ]}
            >
              <Select showSearch onChange={onChangeRole}>
                {userRole.map((role) => (
                  <Option key={role.role_code} value={role.profil}>
                    {role.role_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="pwd_period"
              label="Période de validité mot de passe"
              rules={[
                {
                  required: true,
                  message: "Veuillez sélectionner une période!",
                },
              ]}
            >
              <Select>

                <Option value={30}>30 jours</Option>
                <Option value={60}>60 jours</Option>
                <Option value={90}>90 jours</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="etat"
              label="État"
              rules={[
                { required: true, message: "Veuillez sélectionner un état!" },
              ]}
            >
              <Select>
                <Option value={true}>Actif</Option>
                <Option value={false}>Bloqué</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="new_password"
              label="Mot de passe"
              rules={[
                {
                  required: true,
                  message: "Veuillez saisir un mot de passe!",
                },
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              name="password_confirm"
              label="Confirmation Mot de passe"
              rules={[
                {
                  required: true,
                  message: "Veuillez confirmer le mot de passe!",
                  validateTrigger: "onSubmit",
                  validator: validatePassword,
                },
              ]}
            >
              <Input.Password />
            </Form.Item>
          </>
        )}
        {mode === "edit" && (
          <>
            <Form.Item
              name="login"
              style={{ display: 'none' }}
            >
              <Input hidden />
            </Form.Item>
            <Form.Item
              name="emailaddress"
              label="Email"
              rules={[
                {
                  required: true,
                  type: "email",
                  message: "Veuillez saisir un email valide!",
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="username"
              label="Prénom & Nom"
              rules={[
                {
                  required: true,
                  message: "Veuillez saisir le prénom et le nom!",
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="profil"
              label="Rôle"
              rules={[
                { required: true, message: "Veuillez sélectionner un rôle!" },
              ]}
            >
              <Select showSearch onChange={onChangeRole}>
                {userRole.map((role) => (
                  <Option key={role.role_code} value={role.profil}>
                    {role.role_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="etat"
              label="État"
              rules={[
                { required: true, message: "Veuillez sélectionner un état!" },
              ]}
            >
              <Select>
                <Option value={true}>Actif</Option>
                <Option value={false}>Bloqué</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="pwd_period"
              label="Période de validité mot de passe"
              rules={[
                {
                  required: true,
                  message: "Veuillez sélectionner une période!",
                },
              ]}
            >
              <Select>
                <Option value={10}>10 jours</Option>
                <Option value={30}>30 jours</Option>
                <Option value={60}>60 jours</Option>
                <Option value={90}>90 jours</Option>
              </Select>
            </Form.Item>
          </>
        )}

        {mode === "changePassword" && (
          <>
            <div className="userInfo">
              <p>
                <strong>Login:</strong> <strong>{user?.login}</strong>
              </p>
              <p>
                <strong>Username:</strong><strong> {user?.username}</strong>
              </p>
            </div>
            <Form.Item
              name="login"
              style={{ display: 'none' }}
            >
              <Input hidden />
            </Form.Item>
            <Form.Item
              name="new_password"
              label="Nouveau Mot de passe"
              rules={[
                {
                  required: true,
                  message: "Veuillez saisir un nouveau mot de passe!",
                },
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              name="confirm_password"
              label="Confirmer Mot de passe"
              rules={[
                {
                  required: true,
                  message: "Veuillez confirmer le mot de passe!",
                },
                { validator: validatePassword },
              ]}
            >
              <Input.Password />
            </Form.Item>
          </>
        )}

        {(mode === "add" || mode === "edit") && showSocPointFocal && (
          <Form.Item shouldUpdate noStyle>
            {() => {
              const selectedPointFocals = (
                form.getFieldValue("socPointFocalCodes") || []
              ).map(String);
              const availablePointFocals =
                societeOptions.length > 0 ? societeOptions : (user?.socPointFocals || []);

              return (
                <Form.Item
                  name="socPointFocalCodes"
                  label="Sociétés Autorisées"
                >
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder="Sélectionner les Sociétés Autorisées"
                    optionFilterProp="label"
                  >
                    {availablePointFocals.map((pointFocal) => {
                      const optionCode = pointFocal.codadht ?? pointFocal.codeadht;
                      const optionLabel = `${optionCode} - ${pointFocal.intitule || ""}`;
                      const isChecked = selectedPointFocals.includes(
                        String(optionCode)
                      );

                      return (
                        <Option
                          key={optionCode}
                          value={optionCode}
                          label={optionLabel}
                        >
                          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input type="checkbox" checked={isChecked} readOnly />
                            {optionLabel}
                          </span>
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              );
            }}
          </Form.Item>
        )}
        <Form.Item>
          <Button onClick={onCloseDrawer} style={{ marginRight: 8 }}>
            Annuler
          </Button>
          <Button type="primary" htmlType="submit">
            {mode === "changePassword" || mode === "edit"
              ? "Modifier"
              : "Enregistrer"}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default SideBarUser;
